import { createContext, type ReactNode, use, useCallback, useMemo, useReducer } from 'react';
import {
  snackbarInitialState,
  type SnackbarPayloadType,
  SnackbarReducer,
  type SnackbarType,
} from '../SnackbarReducer';

export type SnackbarReduceType = {
  snackbarState: SnackbarType;
  showSnackbar: (param: SnackbarPayloadType) => void;
  resetSnackbar: () => void;
  hideSnackbar: () => void;
};
export const SnackbarContext = createContext<SnackbarReduceType | null>(null);

type SnackbarProviderProps = {
  children?: ReactNode;
};

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbarState, snackbarDispatch] = useReducer(SnackbarReducer, snackbarInitialState);

  const showSnackbar = useCallback(
    ({
      title,
      description,
      severity,
      autoHide,
      hideDuration,
      small,
      onHide,
    }: SnackbarPayloadType) => {
      if (!snackbarState.show) {
        snackbarDispatch({
          type: 'show',
          payload: {
            title,
            description,
            severity,
            autoHide,
            hideDuration,
            small,
            onHide,
          },
        });
      }
    },
    [snackbarState.show],
  );

  const resetSnackbar = useCallback(() => {
    snackbarDispatch({ type: 'reset' });
  }, []);

  const hideSnackbar = useCallback(() => {
    if (snackbarState.show) {
      snackbarState.onHide?.();
      snackbarDispatch({ type: 'hide' });
    }
  }, [snackbarState]);

  const snackbarValue = useMemo(() => {
    return {
      snackbarState,
      showSnackbar,
      resetSnackbar,
      hideSnackbar,
    };
  }, [hideSnackbar, resetSnackbar, showSnackbar, snackbarState]);

  return <SnackbarContext value={snackbarValue}>{children}</SnackbarContext>;
};

export const useSnackbar = () => {
  const context = use(SnackbarContext);

  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }

  return context;
};
